import type { ExplicitRating, GifReference } from './gif'

/**
 * AI-proposed values for hand-curated `gifs`-doc fields, produced at analysis
 * time and shown on the review card so the curator can confirm/edit them at
 * approval. These are NOT curator-owned on the retrieval record: every
 * re-analysis may re-propose fresh values (unlike {@link RetrievalRecord.curatorHints}).
 *
 * Parsed tolerantly — a record analyzed with the old prompt shape (no
 * proposedMeta) is entirely valid, so every sub-field is optional.
 */
export interface RetrievalProposedMeta {
  /** Proposed content rating; validated against the ExplicitRating enum. */
  explicitRating?: ExplicitRating
  /** Proposed reference {source, keywords, year}, coerced defensively. */
  reference?: GifReference
}

/**
 * The AI-facing representation of a GIF, stored in the top-level
 * `gifRetrieval` collection with doc id === gif id. Lean, dense,
 * signal-only: embedded once at ingest, and sent to the reranker as-is.
 *
 * Lives in its own collection (not on the gif doc) because the client
 * subscribes to the entire `gifs` collection and must not download
 * embeddings with it. The shared doc id is the pointer between the two.
 */
export interface RetrievalRecord {
  gifId: string

  /** 2–4 sentence "what happens" — replaces the thin display `desc`. */
  narrative: string
  /** 3–5 concrete uses, second person ("Use when your friend…"). */
  scenarioExamples: string[]
  /** When sending this GIF would be wrong or annoying. Reranker vetoes. */
  exclusionConditions: string[]
  /** What sets this GIF apart from others in its emotional bucket. */
  distinctiveFeatures: string[]
  /** 1 = catch-all usable anywhere … 5 = hyper-specific. The anti-generic lever. */
  specificityScore: 1 | 2 | 3 | 4 | 5
  /** Emotions/situations this works for. High breadth + low specificity = penalty. */
  breadthTags: string[]
  toneAffordances: 'sincere' | 'sarcastic' | 'both'
  perspectiveAffordances: 'User Voice' | 'Mimicry' | 'both'

  // Hard-filter fields denormalized from the display record so retrieval
  // never has to join back to the `gifs` collection.
  explicitRating: ExplicitRating
  durationSeconds: number | null

  /**
   * Composite embedding over the synthesized retrieval text. Stored via
   * FieldValue.vector(); reads may surface as VectorValue or number[].
   */
  embedding: number[]
  embeddingModel: string
  /** Bump to force re-embedding of all records. */
  embeddingVersion: string
  /** Version of the retrieval ingest prompt. Bump to force re-analysis. */
  promptVersion: string
  /**
   * Exact model id that produced the analysis. Changing the configured
   * analysis model invalidates records (enrichment re-runs them). Optional:
   * pre-2026-07-06 records (Sonnet 4.6 frames era) lack it.
   */
  analysisModel?: string
  analyzedAt: string

  /**
   * Persistent curator correction notes. Authored on /admin/retrieval-review
   * when the automatic analysis mischaracterizes the GIF. Injected into the
   * analysis prompt on every future re-analysis (callable + batch enrichment)
   * and re-saved, so a correction survives re-enrichment forever until edited.
   */
  curatorHints?: string
  /**
   * ISO timestamp of the last curator approval. A record is APPROVED iff
   * `reviewedAt` exists and is >= `analyzedAt` — so any re-analysis (which
   * bumps `analyzedAt`) automatically makes the record stale/unreviewed again.
   * See {@link isRecordApproved}.
   */
  reviewedAt?: string
  /**
   * ISO timestamp of the FIRST-ever curator approval. Set once (on the initial
   * approve) and then preserved forever, through every re-analysis — unlike
   * `reviewedAt`, a stale re-review never un-publishes a live gif. Curator-owned
   * (see mergeCuratorFields). A record is PUBLISHED (searchable + recommendable)
   * once this is set — see {@link isRecordPublished} for the legacy fallback.
   */
  publishedAt?: string
  /**
   * AI-proposed values for hand-curated `gifs`-doc fields (rating + reference),
   * written at analysis time and confirmed by the curator at approval. NOT
   * curator-owned — each re-analysis re-proposes. See {@link RetrievalProposedMeta}.
   */
  proposedMeta?: RetrievalProposedMeta

  /**
   * MOVE-SPACE (Stage 0, additive, opt-in — see
   * docs/architecture/retrieval-move-space.md). 2–4 topic-stripped descriptions
   * of the reaction moves this GIF can perform (including ironic/repurposed
   * uses), each paired with its own embedding. Derived cheaply from the record
   * text by enrich-move-space (no video re-analysis) and REGISTER-LOCKED to the
   * query-side describe-response-moves output — that shared register IS the move
   * space. Optional: records without it degrade to topic-only scoring, so
   * partial enrichment never breaks retrieval.
   *
   * FIRESTORE SHAPE: an array of `{ text, embedding }` maps, NOT parallel
   * `string[]` + `number[][]` fields. Firestore forbids directly nested arrays
   * (a `number[][]` field is illegal), so each vector lives one map-level deep.
   * Stored as a plain `number[]` (not FieldValue.vector) because move scoring is
   * in-memory cosine, not native findNearest — and VectorValue can't nest in an
   * array of maps either. These vectors must never cross the wire (stripped from
   * RetrievalReviewItem, same as `embedding`).
   */
  moves?: Array<{ text: string; embedding: number[] }>
  /**
   * Version of the gif-move-descriptions prompt (and its version-locked twin
   * describe-response-moves) that produced `moves`. Tracks MOVE_PROMPT_VERSION
   * in move-descriptions.service; enrich-move-space skips records already at the
   * current version. Bump the constant when either paired prompt changes.
   */
  movePromptVersion?: string
}

/**
 * A retrieval record is APPROVED when the curator has reviewed it at or after
 * its most recent analysis. Re-analysis bumps `analyzedAt`, so an old approval
 * no longer counts (the record went stale) until the curator re-approves.
 *
 * Pure helper — the single source of truth for approval/staleness across the
 * callables, the review UI, and tests. `record` may carry a serialized subset
 * (no embedding), hence the loose Pick type.
 */
export function isRecordApproved(
  record: Pick<RetrievalRecord, 'analyzedAt' | 'reviewedAt'>
): boolean {
  if (!record.reviewedAt) return false
  return record.reviewedAt >= record.analyzedAt
}

/**
 * A retrieval record is PUBLISHED — eligible for search + recommendation — once
 * a curator has approved it at least once (`publishedAt` set on first approval,
 * preserved forever thereafter).
 *
 * LEGACY CLAUSE: the 369 pre-migration records were approved before
 * `publishedAt` existed, so they carry `reviewedAt` but no `publishedAt`. Until
 * the migration backfills them we treat has-ever-been-reviewed as published.
 * This clause can be dropped once `migrate:unified` has run (every approved
 * record will then have `publishedAt`).
 */
export function isRecordPublished(
  record: Pick<RetrievalRecord, 'publishedAt' | 'reviewedAt'>
): boolean {
  if (record.publishedAt) return true
  // Legacy: reviewed-but-not-yet-backfilled records count as published.
  return !!record.reviewedAt
}

/**
 * A record is STALE when it was reviewed once but a later re-analysis
 * invalidated that approval (reviewed, but reviewedAt < analyzedAt). This is
 * distinct from never-reviewed: the UI badges the two differently.
 */
export function isRecordStale(
  record: Pick<RetrievalRecord, 'analyzedAt' | 'reviewedAt'>
): boolean {
  return !!record.reviewedAt && record.reviewedAt < record.analyzedAt
}

/** Review-status filter buckets for the retrieval-review UI + callable. */
export type RetrievalReviewFilter = 'unreviewed' | 'approved' | 'all'

/**
 * Why a gif was flagged as a potential duplicate of the one under review.
 * Ordered by strength/precedence in {@link REASON_PRECEDENCE}: exact-input-hash
 * (identical original bytes) is the strongest, semantic (embedding near-dup) the
 * weakest. Surfaced as a per-reason chip on the Curation warning card.
 */
export type DuplicateReason =
  | 'exact-input-hash'
  | 'exact-output-hash'
  | 'same-platform-id'
  | 'same-source-url'
  | 'semantic'

/**
 * One potential-duplicate hit shown on the review card so the curator can eyeball
 * it and make the final call (never a hard block). Computed live at review time
 * — carries display info only, NEVER an embedding (vectors never cross the wire).
 */
export interface DuplicateCandidate {
  /** Id of the existing gif that looks like a duplicate. */
  gifId: string
  /** Display url of the candidate gif (for the warning thumbnail). */
  gifUrl: string | null
  /** Display title/narrative of the candidate gif. */
  title: string | null
  /** Primary reason, chosen from {@link reasons} by {@link REASON_PRECEDENCE}. */
  reason: DuplicateReason
  /**
   * Cosine similarity to the reviewed gif when embeddings were compared; 1 for
   * pure exact-identity hits with no vector. The UI shows this for `semantic`.
   */
  score: number
  /** Every reason this candidate matched, precedence-sorted (primary first). */
  reasons: DuplicateReason[]
}

/**
 * A retrieval record serialized for the client: identical to {@link RetrievalRecord}
 * but WITHOUT any embeddings — the 1536-float `embedding` AND the per-move
 * vectors in `moves` (vectors never ship over the wire) — joined with minimal
 * display info from the `gifs` doc so the UI can render the GIF.
 */
export type RetrievalReviewItem = Omit<RetrievalRecord, 'embedding' | 'moves'> & {
  /**
   * Display + curator-truth fields joined from the `gifs` doc for rendering the
   * review card. `explicitRating`/`reference` here are the CONFIRMED catalog
   * values (the review card shows these in precedence over the record's
   * `proposedMeta`); `processingStatus` drives the Incoming section; `publishedAt`
   * powers the "unpublished" badge.
   */
  gif: {
    gifUrl: string | null
    title: string | null
    width: number | null
    height: number | null
    /** Confirmed rating on the gifs doc (undefined if never set). */
    explicitRating?: ExplicitRating
    /** Confirmed reference on the gifs doc (undefined if never set). */
    reference?: GifReference
    /** First-approval timestamp on the gifs doc (undefined = never published). */
    publishedAt?: string
    /** Ingestion pipeline status (completed / pending_analysis / …). */
    processingStatus?: string
  } | null
  /**
   * Potential duplicates of this gif, computed live in the review read path
   * (see RetrievalReviewService.list). Present (possibly empty) only on RETURNED
   * items; the Curation card renders a warning when non-empty. Never persisted.
   */
  duplicateCandidates?: DuplicateCandidate[]
}

/** The subset of retrieval-record fields the curator may hand-edit in the UI. */
export interface RetrievalManualEdits {
  narrative?: string
  scenarioExamples?: string[]
  exclusionConditions?: string[]
  distinctiveFeatures?: string[]
  specificityScore?: 1 | 2 | 3 | 4 | 5
  breadthTags?: string[]
  toneAffordances?: RetrievalRecord['toneAffordances']
  perspectiveAffordances?: RetrievalRecord['perspectiveAffordances']
}

/**
 * What the ingest LLM produces — the retrieval record minus identifiers,
 * embedding, and fields copied from the display record.
 */
export type RetrievalAnalysis = Omit<
  RetrievalRecord,
  | 'gifId'
  | 'explicitRating'
  | 'durationSeconds'
  | 'embedding'
  | 'embeddingModel'
  | 'embeddingVersion'
  | 'promptVersion'
  | 'analysisModel'
  | 'analyzedAt'
  | 'curatorHints'
  | 'reviewedAt'
  | 'publishedAt'
  // proposedMeta is returned alongside the analysis (RetrievalAnalysisResult),
  // not inside it, so it never leaks into the embedding text or the
  // revise-previous-record JSON.
  | 'proposedMeta'
  // moves/movePromptVersion are derived in a separate downstream pass
  // (enrich-move-space) from the record text, not produced by the ingest LLM.
  | 'moves'
  | 'movePromptVersion'
>

export const GIF_RETRIEVAL_COLLECTION = 'gifRetrieval'

// ---------------------------------------------------------------------------
// Retrieval-review callable request/response contracts
// ---------------------------------------------------------------------------

/** Request for `listRetrievalReview`. */
export interface ListRetrievalReviewRequest {
  /** Which review bucket to return. Defaults to 'unreviewed'. */
  filter?: RetrievalReviewFilter
}

export interface ListRetrievalReviewResponse {
  success: boolean
  data?: {
    /** Records (embedding stripped) matching the filter, newest analysis first. */
    items: RetrievalReviewItem[]
    counts: {
      total: number
      approved: number
      /** Never-reviewed OR stale (reviewed then re-analyzed). */
      unreviewed: number
    }
  }
  error?: string
}

/**
 * Request for `approveRetrievalRecord`. Approval is also publication: on the
 * first approve `publishedAt` is stamped and the record's search prose is
 * denormalized onto the gifs doc. The two optional fields are the curator's
 * confirmed values for the hand-curated gifs-doc fields (defaulted from
 * {@link RetrievalProposedMeta} on the review card).
 */
export interface ApproveRetrievalRecordRequest {
  gifId: string
  /** Curator-confirmed content rating. Written to gifs doc + retrieval record. */
  explicitRating?: ExplicitRating
  /** Curator-confirmed reference {source, keywords, year}. Written to gifs doc. */
  reference?: GifReference
}

export interface ApproveRetrievalRecordResponse {
  success: boolean
  data?: { item: RetrievalReviewItem }
  error?: string
}

/**
 * Request for `reanalyzeRetrievalRecord`. Two mutually-exclusive modes:
 *  - hints mode: pass `hints` (the FULL desired curatorHints text; replace
 *    semantics). Re-runs native-video analysis with the hints injected,
 *    rebuilds the embedding, and saves — approval is NOT set (the curator
 *    approves after inspecting the result).
 *  - manualEdits mode: pass `manualEdits`. Applies the field edits, REBUILDS
 *    the embedding from the edited text, and saves. Also does not auto-approve.
 * If both are present, hints mode wins (a re-analysis supersedes stale edits).
 */
export interface ReanalyzeRetrievalRecordRequest {
  gifId: string
  hints?: string
  manualEdits?: RetrievalManualEdits
}

export interface ReanalyzeRetrievalRecordResponse {
  success: boolean
  data?: { item: RetrievalReviewItem }
  error?: string
}
